import { useQuery } from '@tanstack/react-query';
import QualAPI from 'utils/QualAPI';

// Define query keys for subscriptions
export const subscriptionKeys = {
  all: ['subscriptions'],
  plans: ['plans'],
  seats: (subscriptionId) => ['seats', subscriptionId],
};

/**
 * Hook to fetch all subscriptions
 * @returns {Object} The query result containing subscriptions data
 */
export function useSubscriptions() {
  return useQuery({
    queryKey: subscriptionKeys.all,
    queryFn: () => QualAPI.fetchSubscriptions().then(response => response.data),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch the current user's subscription (first subscription)
 * @returns {Object} The query result with the subscription object directly
 */
export function useSubscription() {
  const query = useSubscriptions();
  
  return {
    ...query,
    data: query.data?.subscriptions?.[0] || null,
  };
}

/**
 * Hook to fetch available plans
 * @returns {Object} The query result containing plans data
 */
export function usePlans() {
  return useQuery({
    queryKey: subscriptionKeys.plans,
    queryFn: () => QualAPI.fetchPlans().then(response => response.data),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch seats for a subscription
 * @param {string} subscriptionId - The subscription ID
 * @returns {Object} The query result containing seats data
 */
export function useSeats(subscriptionId) {
  return useQuery({
    queryKey: subscriptionKeys.seats(subscriptionId),
    queryFn: () => QualAPI.fetchSeats(subscriptionId).then(response => response.data),
    enabled: !!subscriptionId,
  });
}